import { ContactInfo, EmbedSettingsResponseDto, ProcessCard } from "@/lib/interfaces/embed";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type IframeSettingsDesignDraft = {
  color: string;
  showImage: boolean;
  customCss: string;
};

export type IframeSettingsTextDraft = {
  heroEyebrowText: string;
  processTitle: string;
  processDescription: string;
  processCards: ProcessCard[];
  contact: ContactInfo;
};

function designFromEmbed(s: EmbedSettingsResponseDto): IframeSettingsDesignDraft {
  return {
    color: s.color ?? "#000000",
    showImage: s.showImage ?? true,
    customCss: s.customCss ?? "",
  };
}

function textFromEmbed(s: EmbedSettingsResponseDto): IframeSettingsTextDraft {
  return {
    heroEyebrowText: s.heroEyebrowText ?? "",
    processTitle: s.process?.title ?? "",
    processDescription: s.process?.description ?? "",
    processCards: s.processCards ?? [],
    contact: {
      title: s.contact?.title ?? "",
      description: s.contact?.description ?? "",
    },
  };
}

type Ctx = {
  designDraft: IframeSettingsDesignDraft;
  textDraft: IframeSettingsTextDraft;
  setDesignDraft: (v: IframeSettingsDesignDraft) => void;
  setTextDraft: (v: IframeSettingsTextDraft) => void;
};

const IframeSettingsPreviewContext = createContext<Ctx | null>(null);

export function IframeSettingsPreviewProvider({
  embedSettings,
  children,
}: {
  embedSettings: EmbedSettingsResponseDto;
  children: ReactNode;
}) {
  const [designDraft, setDesignDraft] = useState<IframeSettingsDesignDraft>(() => designFromEmbed(embedSettings));
  const [textDraft, setTextDraft] = useState<IframeSettingsTextDraft>(() => textFromEmbed(embedSettings));

  const setDesignDraftStable = useCallback((v: IframeSettingsDesignDraft) => {
    setDesignDraft(v);
  }, []);

  const setTextDraftStable = useCallback((v: IframeSettingsTextDraft) => {
    setTextDraft(v);
  }, []);

  const value = useMemo(
    () => ({
      designDraft,
      textDraft,
      setDesignDraft: setDesignDraftStable,
      setTextDraft: setTextDraftStable,
    }),
    [designDraft, textDraft, setDesignDraftStable, setTextDraftStable],
  );

  return <IframeSettingsPreviewContext.Provider value={value}>{children}</IframeSettingsPreviewContext.Provider>;
}

export function useIframeSettingsPreview() {
  const c = useContext(IframeSettingsPreviewContext);
  if (!c) {
    throw new Error("useIframeSettingsPreview must be used within IframeSettingsPreviewProvider");
  }
  return c;
}
